import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import MapComponent from '../components/MapComponent';
import PanicButton from '../components/PanicButton';
import ShareTripButton from '../components/ShareTripButton';
import { RatingModal } from '../components/RatingModal';
import { TrackingIndicator, ConnectionStatus } from '../components/TrackingStatus';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { rideAPI } from '../lib/api';
import { useAuth } from '../lib/auth-context';
import { useCountry } from '../lib/country-context';
import { useTracking } from '../lib/useTracking';
import { toast } from 'sonner';
import { 
  Car, 
  MapPin, 
  Navigation, 
  Clock, 
  DollarSign,
  Users,
  Bike,
  Crown,
  ArrowRight,
  X,
  CheckCircle2,
  Crosshair,
  Loader2,
  Shield
} from 'lucide-react';

const vehicleTypes = [
  { id: 'economy', name: 'Económico', icon: Car, multiplier: 1 },
  { id: 'comfort', name: 'Comfort', icon: Car, multiplier: 1.5 },
  { id: 'premium', name: 'Premium', icon: Crown, multiplier: 2.5 },
  { id: 'xl', name: 'XL', icon: Users, multiplier: 2 },
  { id: 'moto', name: 'Moto', icon: Bike, multiplier: 0.6 },
];

const RideStatusBadge = ({ status }) => {
  const statusConfig = {
    pending: { color: 'bg-yellow-100 text-yellow-600', label: 'Buscando conductor' },
    accepted: { color: 'bg-blue-100 text-blue-600', label: 'Conductor en camino' },
    arrived: { color: 'bg-purple-100 text-purple-600', label: 'Conductor llegó' },
    in_progress: { color: 'bg-green-100 text-green-600', label: 'En viaje' },
    completed: { color: 'bg-teal-100 text-teal-600', label: 'Completado' },
    cancelled: { color: 'bg-red-100 text-red-600', label: 'Cancelado' },
  };
  
  const config = statusConfig[status] || statusConfig.pending;
  
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-medium ${config.color}`}>
      {config.label}
    </span>
  );
};

export default function RidePage() {
  const { user, refreshUser } = useAuth();
  const { country, formatCurrency, convertFromUSD } = useCountry();
  const [step, setStep] = useState('location');
  const [pickupLocation, setPickupLocation] = useState(null);
  const [destinationLocation, setDestinationLocation] = useState(null);
  const [pickupAddress, setPickupAddress] = useState('');
  const [destinationAddress, setDestinationAddress] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState('economy');
  const [selectingLocation, setSelectingLocation] = useState(null);
  const [rides, setRides] = useState([]);
  const [activeRide, setActiveRide] = useState(null);
  const [loading, setLoading] = useState(false);
  const [estimatedPrice, setEstimatedPrice] = useState(0);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [completedRide, setCompletedRide] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);

  // Real-time tracking
  const { 
    isConnected, 
    driverLocation, 
    eta, 
    status: trackingStatus 
  } = useTracking(activeRide?.id, {
    autoConnect: !!activeRide,
    onStatusChange: (newStatus) => {
      if (newStatus === 'completed') {
        setCompletedRide(activeRide);
        setShowRatingModal(true);
        setActiveRide(null);
        setStep('location');
        loadRides();
      }
    }
  });

  useEffect(() => {
    loadRides();
  }, []);

  useEffect(() => {
    if (pickupLocation && destinationLocation) {
      const distance = calculateDistance(pickupLocation, destinationLocation);
      const vehicle = vehicleTypes.find(v => v.id === selectedVehicle);
      const basePrice = 2.5 + (distance * 0.8);
      setEstimatedPrice((basePrice * (vehicle?.multiplier || 1)).toFixed(2));
    }
  }, [pickupLocation, destinationLocation, selectedVehicle]);

  const calculateDistance = (loc1, loc2) => {
    const R = 6371;
    const dLat = (loc2.lat - loc1.lat) * Math.PI / 180;
    const dLon = (loc2.lng - loc1.lng) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(loc1.lat * Math.PI / 180) * Math.cos(loc2.lat * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  };

  const loadRides = async () => {
    try {
      const response = await rideAPI.getAll();
      setRides(response.data);
      const active = response.data.find(r => ['pending', 'accepted', 'arrived', 'in_progress'].includes(r.status));
      if (active) {
        setActiveRide(active);
        setStep('tracking');
      }
    } catch (error) {
      console.error('Error loading rides:', error);
    }
  };

  const handleMapClick = (location) => {
    console.log('[RidePage] Map click received:', location, 'Mode:', selectingLocation);
    if (selectingLocation === 'pickup') {
      setPickupLocation(location);
      setPickupAddress(`${location.lat.toFixed(4)}, ${location.lng.toFixed(4)}`);
      setSelectingLocation(null);
      toast.success('Punto de recogida establecido');
    } else if (selectingLocation === 'destination') {
      setDestinationLocation(location);
      setDestinationAddress(`${location.lat.toFixed(4)}, ${location.lng.toFixed(4)}`);
      setSelectingLocation(null);
      toast.success('Destino establecido');
    }
  };

  const getCurrentLocation = (type) => {
    if (!navigator.geolocation) {
      toast.error('Tu navegador no soporta geolocalización');
      return;
    }
    
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        
        if (type === 'pickup') {
          setPickupLocation(location);
          setPickupAddress(`${location.lat.toFixed(4)}, ${location.lng.toFixed(4)}`);
          toast.success('Ubicación actual establecida como punto de recogida');
        } else {
          setDestinationLocation(location);
          setDestinationAddress(`${location.lat.toFixed(4)}, ${location.lng.toFixed(4)}`);
          toast.success('Ubicación actual establecida como destino');
        }
        setGettingLocation(false);
      },
      (error) => {
        console.error('Geolocation error:', error);
        toast.error('No se pudo obtener tu ubicación. Por favor selecciona en el mapa.');
        setGettingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleRequestRide = async () => {
    if (!pickupLocation || !destinationLocation) {
      toast.error('Selecciona origen y destino');
      return;
    }

    setLoading(true);
    try {
      const response = await rideAPI.create({
        pickup_location: { ...pickupLocation, address: pickupAddress },
        destination: { ...destinationLocation, address: destinationAddress },
        vehicle_type: selectedVehicle,
        payment_method: 'wallet',
        currency: 'USD',
      });
      
      setActiveRide(response.data);
      setStep('tracking');
      toast.success('¡Viaje solicitado! Buscando conductor...');
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al solicitar viaje');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRide = async () => {
    if (!activeRide) return;
    
    try {
      await rideAPI.updateStatus(activeRide.id, 'cancelled');
      setActiveRide(null);
      setStep('location');
      loadRides();
      toast.info('Viaje cancelado');
    } catch (error) {
      toast.error('Error al cancelar');
    }
  };

  // Simulate ride completion for demo
  const handleCompleteRide = async () => {
    if (!activeRide) return;
    
    try {
      await rideAPI.updateStatus(activeRide.id, 'completed');
      setCompletedRide(activeRide);
      setShowRatingModal(true);
      setActiveRide(null);
      setStep('location');
      loadRides();
      toast.success('¡Viaje completado!');
    } catch (error) {
      toast.error('Error al completar');
    }
  };

  const handleRatingSuccess = () => {
    setShowRatingModal(false);
    setCompletedRide(null);
  };

  const handleRatingSkip = () => {
    setShowRatingModal(false);
    // Will be prompted again on next login or service
  };

  return (
    <Layout>
      <div className="h-[calc(100vh-4rem)] lg:h-screen flex flex-col lg:flex-row">
        {/* Map Section */}
        <div className="flex-1 relative">
          <MapComponent
            pickupLocation={pickupLocation || (activeRide?.pickup_location ? { lat: activeRide.pickup_location.lat, lng: activeRide.pickup_location.lng } : null)}
            destinationLocation={destinationLocation || (activeRide?.destination ? { lat: activeRide.destination.lat, lng: activeRide.destination.lng } : null)}
            driverLocation={driverLocation ? { lat: driverLocation.latitude, lng: driverLocation.longitude } : null}
            onMapClick={handleMapClick}
            showRoute={pickupLocation && destinationLocation}
            className="h-64 lg:h-full"
          />
          
          {/* Real-time tracking indicator */}
          {step === 'tracking' && activeRide && (
            <TrackingIndicator 
              status={trackingStatus || activeRide.status}
              eta={eta}
              isConnected={isConnected}
            />
          )}
          
          {/* Location selection indicator */}
          {selectingLocation && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-white shadow-lg px-4 py-2 rounded-xl z-20">
              <p className="text-slate-800 text-sm font-medium">
                Toca el mapa para seleccionar {selectingLocation === 'pickup' ? 'origen' : 'destino'}
              </p>
            </div>
          )}
        </div>

        {/* Panic Button - Only visible during active ride */}
        {activeRide && ['accepted', 'arrived', 'in_progress'].includes(activeRide.status) && (
          <PanicButton
            serviceType="ride"
            serviceId={activeRide.id}
            userLocation={pickupLocation || { lat: 19.4326, lng: -99.1332 }}
          />
        )}

        {/* Share Trip Button - Visible during active ride */}
        {activeRide && ['pending', 'accepted', 'arrived', 'in_progress'].includes(activeRide.status) && (
          <div className="fixed bottom-24 right-4 z-40">
            <ShareTripButton tripId={activeRide.id} tripType="ride" />
          </div>
        )}

        {/* Control Panel - Light Mode */}
        <div className="w-full lg:w-96 bg-white border-t lg:border-t-0 lg:border-l border-slate-200 overflow-y-auto">
          {step === 'location' && (
            <div className="p-6 space-y-6 animate-fade-in">
              <div>
                <h2 className="text-2xl font-bold text-slate-800 font-['Outfit']">Solicitar Viaje</h2>
                <p className="text-slate-500 text-sm mt-1">¿A dónde vamos?</p>
              </div>

              {/* Location Inputs */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-slate-700">Punto de recogida</Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs text-[#00BFA6] hover:text-[#00BFA6]/80 h-auto py-1"
                      onClick={() => getCurrentLocation('pickup')}
                      disabled={gettingLocation}
                      data-testid="use-my-location-btn"
                    >
                      {gettingLocation ? (
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      ) : (
                        <Crosshair className="w-3 h-3 mr-1" />
                      )}
                      Usar mi ubicación
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#00BFA6]" />
                      <Input
                        placeholder="Selecciona en el mapa"
                        value={pickupAddress}
                        onChange={(e) => setPickupAddress(e.target.value)}
                        className="pl-11 h-12 bg-slate-50 border-slate-200 text-slate-800 rounded-xl"
                        data-testid="pickup-input"
                      />
                    </div>
                    <Button
                      variant="outline"
                      className={`h-12 px-4 rounded-xl border-slate-200 ${
                        selectingLocation === 'pickup' ? 'bg-[#00BFA6]/10 border-[#00BFA6] text-[#00BFA6]' : 'text-slate-500'
                      }`}
                      onClick={() => setSelectingLocation(selectingLocation === 'pickup' ? null : 'pickup')}
                      data-testid="select-pickup-btn"
                    >
                      <MapPin className="w-5 h-5" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-700">Destino</Label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Navigation className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#FFD166]" />
                      <Input
                        placeholder="Selecciona en el mapa"
                        value={destinationAddress}
                        onChange={(e) => setDestinationAddress(e.target.value)}
                        className="pl-11 h-12 bg-slate-50 border-slate-200 text-slate-800 rounded-xl"
                        data-testid="destination-input"
                      />
                    </div>
                    <Button
                      variant="outline"
                      className={`h-12 px-4 rounded-xl border-slate-200 ${
                        selectingLocation === 'destination' ? 'bg-[#FFD166]/10 border-[#FFD166] text-[#FFD166]' : 'text-slate-500'
                      }`}
                      onClick={() => setSelectingLocation(selectingLocation === 'destination' ? null : 'destination')}
                      data-testid="select-destination-btn"
                    >
                      <Navigation className="w-5 h-5" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Vehicle Selection */}
              {pickupLocation && destinationLocation && (
                <div className="space-y-3 animate-slide-up">
                  <Label className="text-slate-700">Tipo de vehículo</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {vehicleTypes.map((vehicle) => (
                      <button
                        key={vehicle.id}
                        onClick={() => setSelectedVehicle(vehicle.id)}
                        className={`p-4 rounded-xl border transition-all ${
                          selectedVehicle === vehicle.id
                            ? 'border-[#00BFA6] bg-[#00BFA6]/10'
                            : 'border-slate-200 hover:border-slate-300 bg-white'
                        }`}
                        data-testid={`vehicle-${vehicle.id}`}
                      >
                        <vehicle.icon className={`w-6 h-6 mx-auto mb-2 ${
                          selectedVehicle === vehicle.id ? 'text-[#00BFA6]' : 'text-slate-400'
                        }`} />
                        <p className={`text-sm font-medium ${
                          selectedVehicle === vehicle.id ? 'text-[#00BFA6]' : 'text-slate-600'
                        }`}>{vehicle.name}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Price Estimate */}
              {pickupLocation && destinationLocation && (
                <div className="bg-slate-50 p-4 rounded-xl animate-slide-up">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-[#00BFA6]" />
                      <span className="text-slate-600">Precio estimado</span>
                    </div>
                    <span className="text-2xl font-bold text-slate-800">
                      {formatCurrency(convertFromUSD(estimatedPrice))}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-2 text-slate-500 text-sm">
                    <Clock className="w-4 h-4" />
                    <span>~10-15 min de espera</span>
                  </div>
                </div>
              )}

              {/* Request Button */}
              <Button
                onClick={handleRequestRide}
                disabled={loading || !pickupLocation || !destinationLocation}
                className="w-full h-14 btn-primary text-lg"
                data-testid="request-ride-btn"
              >
                {loading ? (
                  <div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    Solicitar Viaje
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Tracking View */}
          {step === 'tracking' && activeRide && (
            <div className="p-6 space-y-6 animate-fade-in">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-slate-800 font-['Outfit']">Tu Viaje</h2>
                <div className="flex items-center gap-3">
                  <ConnectionStatus isConnected={isConnected} />
                  <RideStatusBadge status={trackingStatus || activeRide.status} />
                </div>
              </div>

              {/* ETA Display */}
              {eta !== null && eta !== undefined && (
                <Card className="card-elevated bg-[#00BFA6]/5 border-[#00BFA6]/20">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Clock className="w-6 h-6 text-[#00BFA6]" />
                        <span className="text-slate-600">Tiempo estimado</span>
                      </div>
                      <span className="text-3xl font-bold text-slate-800">
                        {eta === 0 ? 'Llegando' : `${eta} min`}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Driver Info */}
              <Card className="card-elevated">
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-[#00BFA6]/10 flex items-center justify-center">
                      <Car className="w-7 h-7 text-[#00BFA6]" />
                    </div>
                    <div className="flex-1">
                      <p className="text-slate-800 font-semibold">{activeRide.driver_name || 'Buscando conductor...'}</p>
                      <p className="text-slate-500 text-sm">{activeRide.vehicle_type}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-slate-800">
                        {formatCurrency(convertFromUSD(activeRide.estimated_price || activeRide.price || 0))}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Route Info */}
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-[#00BFA6] mt-0.5" />
                  <div>
                    <p className="text-slate-500 text-xs">Origen</p>
                    <p className="text-slate-800">{activeRide.pickup_location?.address || 'N/A'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Navigation className="w-5 h-5 text-[#FFD166] mt-0.5" />
                  <div>
                    <p className="text-slate-500 text-xs">Destino</p>
                    <p className="text-slate-800">{activeRide.destination?.address || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Security Monitoring Badge */}
              <div className="p-3 bg-teal-50 border border-teal-100 rounded-xl flex items-center gap-2">
                <Shield className="w-5 h-5 text-teal-600" />
                <div className="flex-1">
                  <p className="text-teal-700 text-sm font-medium">Viaje monitoreado 24/7</p>
                  <p className="text-teal-600 text-xs">Central de Seguridad Mercapoli Move</p>
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-3">
                {/* Demo: Complete Ride Button */}
                {activeRide.status === 'in_progress' && (
                  <Button
                    onClick={handleCompleteRide}
                    className="w-full h-12 bg-green-600 hover:bg-green-700 text-white rounded-xl"
                    data-testid="complete-ride-btn"
                  >
                    <CheckCircle2 className="w-5 h-5 mr-2" />
                    Completar Viaje (Demo)
                  </Button>
                )}
                
                {activeRide.status === 'pending' && (
                  <Button
                    onClick={handleCancelRide}
                    variant="outline"
                    className="w-full h-12 border-red-200 text-red-600 hover:bg-red-50 rounded-xl"
                    data-testid="cancel-ride-btn"
                  >
                    <X className="w-5 h-5 mr-2" />
                    Cancelar Viaje
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Ride History */}
          <div className="p-6 border-t border-slate-200">
            <h3 className="text-lg font-semibold text-slate-800 mb-4 font-['Outfit']">Historial</h3>
            {rides.filter(r => r.status === 'completed' || r.status === 'cancelled').length > 0 ? (
              <div className="space-y-3">
                {rides.filter(r => r.status === 'completed' || r.status === 'cancelled').slice(0, 5).map((ride) => (
                  <div key={ride.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <Car className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-slate-800 text-sm font-medium">{ride.destination?.address?.slice(0, 20) || 'N/A'}...</p>
                        <p className="text-slate-500 text-xs">{new Date(ride.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <RideStatusBadge status={ride.status} />
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-sm text-center py-4">No hay viajes anteriores</p>
            )}
          </div>
        </div>
      </div>

      {/* Rating Modal */}
      <RatingModal
        isOpen={showRatingModal}
        onClose={() => setShowRatingModal(false)}
        serviceType="ride"
        serviceId={completedRide?.id}
        ratedUserId={completedRide?.driver_id}
        ratedUserName={completedRide?.driver_name}
        canSkip={true}
        onSkip={handleRatingSkip}
        onSuccess={handleRatingSuccess}
      />
    </Layout>
  );
}
